/**
 * Options accepted by {@link createElement} for configuring a new DOM node.
 */
export interface CreateElementOptions {
  className?: string
  text?: string
  html?: string
  attributes?: Record<string, string>
}

/**
 * Create a typed HTML element with optional class, text/html content, and
 * attributes. A small convenience to keep the overlay UI code declarative.
 *
 * @template TagName - A known HTML tag name.
 * @param {TagName} tagName - The tag to create.
 * @param {CreateElementOptions} [options] - Optional configuration for the node.
 * @returns {HTMLElementTagNameMap[TagName]} The created element.
 */
export function createElement<TagName extends keyof HTMLElementTagNameMap>(
  tagName: TagName,
  options: CreateElementOptions = {},
): HTMLElementTagNameMap[TagName] {
  const element = document.createElement(tagName)
  if (options.className !== undefined) {
    element.className = options.className
  }
  if (options.text !== undefined) {
    element.textContent = options.text
  }
  if (options.html !== undefined) {
    element.innerHTML = options.html
  }
  if (options.attributes !== undefined) {
    for (const [key, value] of Object.entries(options.attributes)) {
      element.setAttribute(key, value)
    }
  }
  return element
}

/**
 * Detect whether the current device should run the reduced, touch-friendly
 * version of the experience. Used to scale particle counts and pixel ratio.
 *
 * @returns {boolean} True when the device is touch-first or narrow.
 */
export function isMobileDevice(): boolean {
  const isCoarsePointer = window.matchMedia('(pointer: coarse)').matches
  const isNarrowViewport = window.innerWidth < 820
  return isCoarsePointer || isNarrowViewport
}

/**
 * Detect whether the user has asked the operating system to reduce motion.
 *
 * @returns {boolean} True when reduced motion is preferred.
 */
export function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}
